import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The rects a `SubViewportContainer` forces onto its sub-viewports, by node
 * path: the return leg of ADR-0033's seam. With `stretch` on, Godot's
 * `recalc_force_viewport_sizes` sizes the viewport at `get_size() / stretch_shrink`,
 * and only the container's native painter, which solves the Control layout, knows that rect.
 */
import { createContext, useCallback, useContext, useRef, useState } from 'react';
// Separate from the texture registry: the two travel in opposite directions
// with different lifetimes, and a consumer of one must not re-render for the other.
const NO_OP_REGISTER = () => () => { };
const EMPTY = new Map();
const RegisterViewportRectContext = createContext(NO_OP_REGISTER);
RegisterViewportRectContext.displayName = 'RegisterViewportRectContext';
const ViewportRectsContext = createContext(EMPTY);
ViewportRectsContext.displayName = 'ViewportRectsContext';
/** Stable publisher, for `<SubViewportContainer>`'s surface. */
export function useRegisterViewportRect() {
    return useContext(RegisterViewportRectContext);
}
/**
 * The rect forced at `path`, or null, which is valid: with `stretch` off Godot
 * returns early, and a sub-viewport with no container has nothing to resize it.
 * Both keep the authored `size`.
 */
export function useViewportRect(path) {
    const rects = useContext(ViewportRectsContext);
    return path === null ? null : (rects.get(path) ?? null);
}
export function ViewportRectProvider({ children }) {
    const [rects, setRects] = useState(() => new Map());
    /**
     * The registration that owns each path. The entry cannot say, since the fast
     * path below keeps an equal rect's original object. A ref, so a
     * transfer of ownership never renders.
     */
    const owners = useRef(new Map()).current;
    const registerViewportRect = useCallback((path, rect) => {
        // Ownership binds at call time and outside the updater, which must stay
        // pure for StrictMode's double invocation.
        const token = {};
        owners.set(path, token);
        setRects((prev) => {
            // Stable on an unchanged rect: a successor that registers before its
            // predecessor's cleanup republishes it, and a new Map re-renders every consumer.
            const current = prev.get(path);
            if (current && current.x === rect.x && current.y === rect.y)
                return prev;
            const next = new Map(prev);
            next.set(path, rect);
            return next;
        });
        return () => {
            if (owners.get(path) !== token)
                return;
            owners.delete(path);
            setRects((prev) => {
                if (!prev.has(path))
                    return prev;
                const next = new Map(prev);
                next.delete(path);
                return next;
            });
        };
    }, [owners]);
    return (_jsx(RegisterViewportRectContext.Provider, { value: registerViewportRect, children: _jsx(ViewportRectsContext.Provider, { value: rects, children: children }) }));
}
//# sourceMappingURL=ViewportRectContext.js.map